import React, { useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

// REMOVE: const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Theme creation logic remains the same
  const theme = useMemo(() => {
    const mode = 'light'; // Hardcoded
    return createTheme({
      palette: {
        mode: mode,
        primary: {
          main: '#0e4bbd',
          contrastText: '#ffffff',
        },
        secondary: {
          main: '#26b954',
          contrastText: '#ffffff',
        },
        background: {
          default: '#f4f7fa', // Light background
          paper: '#ffffff',   // White paper background
        },
        text: {
          primary: '#1e293b', // Darker text for primary
          secondary: '#64748b', // Lighter text for secondary
        },
        divider: 'rgba(0, 0, 0, 0.08)', // Subtle divider
        error: { main: '#d32f2f' },
        warning: { main: '#ffa000', light: '#ffb74d' },
        success: { main: '#388e3c', light: '#81c784' },
        info: { main: '#1976d2' },
        action: {
          hover: 'rgba(14, 75, 189, 0.06)', // Primary color hover with low opacity
          selected: 'rgba(14, 75, 189, 0.12)', // Primary color selected with more opacity
        },
        grey: { // Keep standard greys
          100: '#f5f5f5',
          400: '#bdbdbd',
          800: '#424242',
          // You might want to add more shades like 50, 200, 300, 500, 600, 700, 900 if needed elsewhere
        },
      },
      typography: {
        // Confirming 'Inter' is the primary font
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        button: { textTransform: 'none', fontWeight: 600 },
        h4: { fontWeight: 700 },
        h6: { fontWeight: 600 },
        // Consider adding other typography variants if needed (h1, h2, h3, h5, body1, body2, etc.)
      },
      shape: {
        borderRadius: 8, // Slightly rounded corners
      },
      components: {
        MuiAppBar: {
          styleOverrides: {
            root: ({ theme }) => ({
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.primary.main,
              boxShadow: 'none',
              borderBottom: `1px solid ${theme.palette.divider}`, // Changed border to match Card/Table
            }),
          },
        },
        MuiButton: {
          styleOverrides: {
            root: { borderRadius: 6, padding: '8px 16px' },
            containedPrimary: ({ theme }) => ({
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: alpha(theme.palette.primary.main, 0.9)
              }
            }),
            containedSecondary: ({ theme }) => ({
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: alpha(theme.palette.secondary.main, 0.9)
              }
            })
          }
        },
        MuiCard: {
          styleOverrides: {
            root: ({ theme }) => ({
              borderRadius: theme.shape.borderRadius, // Use theme shape
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)', // Softer shadow
              border: `1px solid ${theme.palette.divider}`
            })
          }
        },
        MuiCardHeader: {
             styleOverrides: {
                title: ({ theme }) => ({
                    fontWeight: 600, // Make card headers slightly bolder
                    fontSize: '1.1rem',
                }),
             }
        },
        MuiTableCell: {
          styleOverrides: {
            head: ({ theme }) => ({
              backgroundColor: theme.palette.background.default, // Use default background for header
              fontWeight: 600,
              color: theme.palette.text.secondary, // Use secondary text color
              padding: '12px 16px',
              borderBottom: `1px solid ${theme.palette.divider}`,
              textTransform: 'uppercase', // Uppercase headers
              fontSize: '0.75rem', // Smaller header text
            }),
            body: ({ theme }) => ({
              padding: '12px 16px',
              borderBottom: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.primary, // Ensure body text uses primary color
            })
          }
        },
        MuiDataGrid: {
          styleOverrides: {
            root: ({ theme }) => ({
              border: 'none',
              borderRadius: theme.shape.borderRadius, // Add border radius to match cards
              backgroundColor: theme.palette.background.paper, // Match card background
              '& .MuiDataGrid-row:hover': {
                backgroundColor: theme.palette.action.hover,
              },
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.default, // Match table cell header
                 color: theme.palette.text.secondary, // Match table cell header
                 textTransform: 'uppercase', // Match table cell header
                 fontSize: '0.75rem', // Match table cell header
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                 fontWeight: 600, // Match table cell header weight
              },
              '& .MuiDataGrid-cell': {
                borderBottom: `1px solid ${theme.palette.divider}`, // Add subtle cell dividers
                '&:focus': {
                  outline: 'none',
                },
                '&:focus-within': {
                  outline: 'none',
                }
              },
              '& .MuiDataGrid-columnSeparator': {
                display: 'none',
              },
              '& .MuiDataGrid-footerContainer': {
                 borderTop: `1px solid ${theme.palette.divider}`, // Add divider above footer
              }
            })
          }
        },
        MuiTooltip: {
           styleOverrides: {
              tooltip: ({ theme }) => ({
                 backgroundColor: theme.palette.grey[800], // Darker tooltip
                 fontSize: '0.8rem',
              }),
              arrow: ({ theme }) => ({
                 color: theme.palette.grey[800],
              }),
           }
        }
        // Add more component overrides here for Link, TextField, etc. if needed
      }
    });
  }, []); // No dependency needed as mode is static

  // Directly return the MuiThemeProvider with the created theme
  return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>;
};

// REMOVE: export const useTheme = () => useContext(ThemeContext);